import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyEvent, getCompanyEventDocuments } from 'src/api/company';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getCompanyEventAccess } from 'src/helpers/companyEventAccess';

export const Route = createFileRoute('/company_/$groupId/events/$eventId/documents/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			filter: (search.filter as string) || undefined,
			page: (search.page as number) || undefined,
		};
	},
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, eventId } = Route.useParams();
	const searchParams = Route.useSearch();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const query = constructQuery({
		page: searchParams.page || 0,
		all: searchParams.filter === 'all' ? 'yes' : '',
	});

	const { data: event } = useQuery({
		queryKey: ['company-event', groupId, eventId],
		queryFn: () => getCompanyEvent(groupId, eventId),
		enabled: !userLoading,
	});

	const { data: documents, isLoading: docsLoading } = useQuery({
		queryKey: ['company-event-documents', groupId, eventId, searchParams],
		queryFn: () => getCompanyEventDocuments(groupId, eventId, query),
		enabled: !userLoading,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['company-document-types'],
		queryFn: () => getDocumentTypes('events'),
	});

	useBreadcrumbLevel(3, 'Documents');

	if (userLoading || docsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !event) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);
	const { isReadOnlyEvent } = getCompanyEventAccess(event, groupId, authenticatedUser?.id);

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-event-documents', groupId, eventId] });
	};

	return (
		<DocumentTab
			data={documents}
			documentTypes={documentTypes?.data || []}
			revalidateDetail={handleReload}
			onReload={handleReload}
			apiUrl={`/api/company-portal/groups/${groupId}/events/${event.id}/files`}
			onSelectDocument={(id: string) =>
				navigate({
					to: '/company/$groupId/events/$eventId/documents/$documentId',
					params: { groupId, eventId, documentId: id },
				})
			}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.companyDocumentsView]),
				fullView: verifyPermission([PERMISSIONS.companyDocumentsView]),
				upload: !isReadOnlyEvent && verifyPermission([PERMISSIONS.companyDocumentsCreate]),
				archive: !isReadOnlyEvent && verifyPermission([PERMISSIONS.companyDocumentsEdit]),
				edit: !isReadOnlyEvent && verifyPermission([PERMISSIONS.companyDocumentsEdit]),
			}}
		/>
	);
}
